import React from 'react';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import type {AccountListResponse} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {useGetAccountListV2Query} from 'src/redux/queries/account';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useAccountColumns} from './hooks/useAccountColumns';

import {SidebarLinkAccounts} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export const AccountListPage: React.FC = () => {
  useSetDocumentTitle('Accounts');
  const navigate = useAppNavigate();

  const columns = useAccountColumns();

  const tableState = useTableState({
    identifier: 'account-list-page',
    columns,
    defaultColumnVisibility: {},
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'email', desc: false}],
    defaultShowColumnFilters: true,
  });

  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetAccountListV2Query(
      {
        start,
        length,
        filters,
        filterFns,
        sorting,
      },
      {refetchOnMountOrArgChange: true}
    );

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const handleNavigateToAccount = (row: AccountListResponse) =>
    navigate(PathUtility.AccountsView(row.id));

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns,
      data: rows,
      getRowId: row => String(row.id),

      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            onClick={() => handleNavigateToAccount(row.original)}
          />
        </TableRowActions>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <ContentWrapper>
      <PageHeader>
        <PageHeader.Title
          icon={SidebarLinkAccounts.icon}
          subtitle="View user accounts across all instances">
          Accounts
        </PageHeader.Title>
      </PageHeader>
      {table}
    </ContentWrapper>
  );
};

export default AccountListPage;

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.Accounts(),
  element: <AccountListPage />,
  isPrivate: true,
  requireSuperAdmin: true,
});
